import React, { useRef, useEffect, useState, useCallback } from "react";
import { Canvas } from "@react-three/fiber";
import { Center, CameraControls, isWebGL2Available } from "@react-three/drei";
import "./App.css";
import * as THREE from "three";
import { Model } from "./Test_10";

import { WasdControls } from "./WasdControls";
import CameraController, { cameraSetups } from "./CameraController";
import useStore from "./useStore";
import { useShallow } from "zustand/react/shallow";
import "@theme-toggles/react/css/Classic.css";
import { Classic } from "@theme-toggles/react";
import TechCard from "./TechCard";
import { useForm, ValidationError } from "@formspree/react";
import { useInView } from "react-intersection-observer";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ClusterOne from "./components/ClusterOne";

const webGL2Available = isWebGL2Available();

// Example constants for an architectural style in light mode
// const PAPER_BG_COLOR = "#faf8ef";
// const TEXT_LIGHT_COLOR = "#333";
// const WIREFRAME_LIGHT = "#19C0C0"; // wireframe lines in light mode

// Dark mode colors
// const BG_DARK_COLOR = "#000";
// const TEXT_DARK_COLOR = "#ffb700";
// const WIREFRAME_DARK = "#ffb700"; // golden

const LoadingOverlay = () => {
  const [connectionSlow, setConnectionSlow] = React.useState(false);

  React.useEffect(() => {
    // @ts-ignore
    if (navigator.connection) {
      // @ts-ignore
      const connection = navigator.connection.effectiveType;

      if (
        connection === "2g" ||
        connection === "slow-2g" ||
        connection === "3g"
      ) {
        setConnectionSlow(true);
        // Adjust your site behavior (e.g., reduce image quality, disable animations, etc.)
      }
    }
  }, []);

  if (webGL2Available) return <></>;

  return (
    <div className="loading-overlay">
      {connectionSlow ? (
        <p style={{ color: "red" }}>Connection might be slow.</p>
      ) : (
        <></>
      )}
      <>This browser doesn't support this 3D model :(</>
    </div>
  );
};

function ContactForm() {
  const [state, handleSubmit] = useForm("myzkjbbo");
  if (state.succeeded) {
    return <p>You've subscribed to updates.</p>;
  }
  return (
    <form onSubmit={handleSubmit}>
      <input
        id="email"
        type="email"
        name="email"
        placeholder="your@email.com"
      />
      <ValidationError prefix="Email" field="email" errors={state.errors} />
      <textarea id="message" name="message" placeholder="(Optional) Message" />
      <ValidationError prefix="Message" field="message" errors={state.errors} />
      <button type="submit" disabled={state.submitting}>
        Submit
      </button>
    </form>
  );
}

interface SceneWithModelProps {
  darkMode: boolean;
}

function SceneWithModel({ darkMode }: SceneWithModelProps) {
  const modelRef = useRef<THREE.Group>(null);

  useEffect(() => {
    if (!modelRef.current) return;
    // Get wire color from our CSS variable (note: it will differ based on data-theme)
    const wireColor = getComputedStyle(document.documentElement)
      .getPropertyValue("--wireframe-color")
      .trim();

    modelRef.current.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        if (
          object.name.includes("Waves") ||
          object.name.includes("Die") ||
          object.name.includes("Opto")
        ) {
          // Use some base color for wave/die; get text color, for example
          const textColor = getComputedStyle(document.documentElement)
            .getPropertyValue("--text-color")
            .trim();
          object.material = new THREE.MeshBasicMaterial({
            color: textColor,
            transparent: true,
            opacity: 0.8,
          });
        } else {
          // Everything else uses our wireframe color
          if (Array.isArray(object.material)) {
            object.material = object.material.map(() => {
              return new THREE.MeshPhongMaterial({
                wireframe: true,
                color: wireColor,
                emissive: wireColor,
                emissiveIntensity: 0.3,
              });
            });
          } else {
            object.material = new THREE.MeshPhongMaterial({
              wireframe: true,
              color: wireColor,
              emissive: wireColor,
              emissiveIntensity: 0.3,
            });
          }
        }
      }
    });
  }, [darkMode]);

  return (
    <Center position={[-5, -1, 3]}>
      <Model topLevelRef={modelRef} scale={[1, 1, 1]} />
    </Center>
  );
}

function BlogPost({
  date,
  title,
  byline,
  frontpageByline,
  excerpt,
  fullText,
  logo,
  isFrontpage = false,
  className = "",
}: {
  date: string;
  title: string;
  byline: string;
  frontpageByline?: string;
  excerpt: string;
  fullText: string;
  logo?: string | string[];
  isFrontpage?: boolean;
  className?: string;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const logos = Array.isArray(logo) ? logo : logo ? [logo] : [];

  // Special frontpage collapsed view
  if (isFrontpage && !expanded) {
    return (
      <div className={`blog-post-card frontpage ${className}`}>
        <div className="blog-post-header">
          <div className="blog-post-title">{title}</div>
          {frontpageByline && (
            <div className="blog-post-byline">{frontpageByline}</div>
          )}
        </div>
      </div>
    );
  }

  // Regular view (or expanded frontpage view)
  return (
    <div className={`blog-post-card ${className}`}>
      {logos.length > 0 && (
        <div className="blog-post-logo">
          {logos.map((logoSrc, index) => (
            <div key={index} className="blog-post-logo-item">
              <img src={logoSrc} alt="" />
            </div>
          ))}
        </div>
      )}
      <div className="blog-post-header">
        <div className="blog-post-date">{date}</div>
        <div className="blog-post-title">{title}</div>
        <div className="blog-post-byline">{byline}</div>
      </div>
      <div className="blog-post-excerpt">{expanded ? fullText : excerpt}</div>
      {fullText && (
        <div
          className="blog-post-readmore"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "▲ Show less" : "▼ Read more"}
        </div>
      )}
    </div>
  );
}

// Add this near the top of the file, after imports
const UPDATES = [
  {
    date: "February 15, 2025",
    title: "Ulyssean joins the Confidential Computing Consortium",
    byline: "",
    frontpageByline: "Feb 15, 2025",
    excerpt:
      "Ulyssean has joined the Confidential Computing Consortium, a Linux Foundation project to secure data in use and accelerate the adoption of confidential computing through open collaboration. We're joining a group of 50 companies including industry leaders like Nvidia, AMD, Intel, and Microsoft, and look forward to contributing to work on standards and open source implementations. Ulyssean team recently presented our work on side channels in the age of AGI to the consortium's Technical Advisory Council general meeting, February 6th.",
    fullText: "",
  },
  {
    date: "February 9, 2025",
    title: "Paris AI Security Forum",
    byline: "",
    frontpageByline: "Feb 9, 2025",
    excerpt:
      "Forum for frontier security. Participants including Vijay Bolina (Chief Infosec Officer, DeepMind), Zico Kolter (Chair of ML department, CMU & board, OpenAI) and Lisa Einstein (Chief AI Officer, US Cyber Defense Agency), and other representatives from Meta, Anthropic, OpenAI, Stanford, US Berkeley, and more. See https://aisecurity.forum/events/paris-ai-security-forum-25 Ulyssean team is speaking at the event, and also contributed advice and co-organizing support.",
    fullText: "",
  },
  {
    date: "Nov 20, 2024",
    title: "Thank you, Dell and TensTorrent...",
    byline: "",
    excerpt:
      "...for providing A100 and wormhole accelerators for our research sprint on verifiable hardware. More details coming soon.",
    fullText: "",
    // logo: ["/assets/dell.png", "/assets/tenstorrent.png"],
  },
  {
    date: "Nov 20, 2024",
    title: "We built... A Box",
    byline: "Release Note",
    excerpt:
      "Six-week sprint to fit production-grade data-centre hardware in the form factor of a portable hardcase, and build an associated software stack, for an end-to-end demonstration and research platform for flexible hardware-enabled guarantees, in collaboration with Amodo Design. More details coming soon.",
    fullText: "",
  },
  {
    date: "September 8, 2024",
    title: "Verification Tech for US-China Statement",
    byline: "",
    excerpt:
      "Ulyssean contributed technology demonstration for a statement by US and Chinese AI scientists on verification of global agreements on AGI. See event report at https://idais.ai/dialogues/ and tech prototype at https://secure-processor-demo.replit.app/",
    fullText: "",
  },
  {
    date: "September 8, 2024",
    title:
      "Advice on report from UK's Advanced Research and Innovation Agency (ARIA)",
    byline: "",
    excerpt:
      "Ulyssean team members contributed advice to a report commissioned by the UK government's Advanced Research and Innovation Agency (ARIA), on potential security and assurance features of frontier accelerator chips. See: https://web.archive.org/web/20260117071513/https://yoshuabengio.org/wp-content/uploads/2024/09/FlexHEG-Interim-Report_2024.pdf",
    fullText: "",
  },
  {
    date: "August 8, 2024",
    title: "AI Security Forum",
    byline: "DEFCON, Las Vegas",
    excerpt:
      "Forum for frontier security. Speakers include Jason Clinton (Chief Infosec Officer, Anthropic), Mikel Rodriguez (AI Red Teaming lead, DeepMind) and participants from Meta, Anthropic, OpenAI, HuggingFace, the intelligence community, and more. Ulyssean team members co-organized the event with Palisade Research and the AI Risk Mitigation Fund. See https://aisecurity.forum/ai-security-forum-24",
    fullText: "",
  },
];

// Hero is streamed as two pre-defined lines, each its own ScrambleText, the
// second starting when the first finishes. Keeps the scramble effect from
// ever fighting line-wrapping (words blinking across the line boundary).
const heroLine1 = "Rapidly deployable custom chips for";
const heroLine2 = "solving hard problems in AI resilience";

const HeroScramble = ({
  inView,
  onComplete,
}: {
  inView?: boolean;
  onComplete?: () => void;
}) => {
  const [line1Done, setLine1Done] = useState(false);
  // nowrap: line 1 is near-exactly container width, and the random scramble
  // chars are sometimes wider than the chars they stand in for — without
  // nowrap the line momentarily overflows and dumps its tail onto row 2.
  const lineStyle: React.CSSProperties = { whiteSpace: "nowrap" };
  return (
    <>
      <div style={lineStyle}>
        <ScrambleText
          text={heroLine1}
          inView={true}
          speed={20}
          startDelay={400}
          onComplete={() => setLine1Done(true)}
        />
      </div>
      {/* minHeight reserves line 2's row before it starts streaming, so the
          page doesn't hop vertically when it appears. */}
      <div style={{ ...lineStyle, minHeight: "1.2em" }}>
        {line1Done && (
          <ScrambleText
            text={heroLine2}
            inView={true}
            speed={20}
            startDelay={0}
            onComplete={onComplete}
          />
        )}
      </div>
    </>
  );
};

// Create a new Updates component
const Updates = ({
  limit,
  isFrontpage = false,
  handleNavigation,
}: {
  limit?: number;
  isFrontpage?: boolean;
  handleNavigation: (page: Page) => void;
}) => {
  const updates = limit ? UPDATES.slice(0, limit) : UPDATES;
  const hasMore = limit ? UPDATES.length > limit : false;
  const [visiblePosts, setVisiblePosts] = useState<number[]>([]);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      const showPosts = () => {
        updates.forEach((_, index) => {
          setTimeout(() => {
            setVisiblePosts((prev) => [...prev, index]);
          }, index * 200); // 200ms delay between each post
        });
      };
      showPosts();
    }
  }, [inView, updates.length]);

  return (
    <div ref={ref}>
      {updates.map((post, idx) => (
        <BlogPost
          key={idx}
          {...post}
          isFrontpage={isFrontpage}
          className={`blog-post-card ${
            visiblePosts.includes(idx) ? "visible" : ""
          }`}
        />
      ))}
      {hasMore && (
        <div className="view-all-updates">
          <button
            onClick={() => handleNavigation("updates")}
            className="view-updates-button"
          >
            All updates →
          </button>
        </div>
      )}
    </div>
  );
};

const ScrambleText = ({
  text,
  inView,
  speed = 50,
  startDelay = 80,
  onComplete,
}: {
  text: string;
  inView: boolean;
  speed?: number;
  startDelay?: number;
  onComplete?: () => void;
}) => {
  const [displayText, setDisplayText] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldStart, setShouldStart] = useState(false);

  useEffect(() => {
    if (inView && !shouldStart) {
      const timer = setTimeout(() => {
        setShouldStart(true);
      }, startDelay);
      return () => clearTimeout(timer);
    }
  }, [inView, startDelay]);

  useEffect(() => {
    if (shouldStart && !isAnimating) {
      setIsAnimating(true);
      let currentIndex = 0;
      const scrambleChars =
        "abcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?";

      const interval = setInterval(() => {
        if (currentIndex < text.length) {
          setDisplayText((prev) => {
            const revealed = text.slice(0, currentIndex);
            const remainingLength = text.length - currentIndex;
            const scrambleLen = Math.max(0, Math.min(3, remainingLength));
            const scrambled = Array(scrambleLen)
              .fill(0)
              .map(
                () =>
                  scrambleChars[
                    Math.floor(Math.random() * scrambleChars.length)
                  ],
              )
              .join("");
            // Render the not-yet-revealed tail invisibly so the element's
            // line-wrapping is locked to the final text from frame one.
            // Without this, the wrap point shifts as the scramble tail
            // changes width each tick, making words at the line boundary
            // blink between lines.
            const hiddenTail = text.slice(currentIndex + scrambleLen);
            return (
              revealed +
              `<span style="color: gray">${scrambled}</span>` +
              `<span style="visibility: hidden">${hiddenTail}</span>`
            );
          });
          currentIndex++;
        } else {
          setDisplayText(text);
          clearInterval(interval);
          onComplete?.();
        }
      }, speed);

      return () => clearInterval(interval);
    }
  }, [shouldStart, text, speed, onComplete]);

  if (!shouldStart) {
    return <span className="scramble-text"></span>;
  }

  return (
    <span
      className="scramble-text"
      dangerouslySetInnerHTML={{ __html: displayText }}
    />
  );
};

const ValueItem = ({
  number,
  keyword,
  content,
}: {
  number: string;
  keyword: string;
  content: string;
}) => {
  return (
    <div className="value-item">
      <div className="value-number">{number}</div>
      <div className="value-content">
        <p>
          <span className="value-keyword">{keyword} </span>: {content}
          {/* <ScrambleText text={content} inView={inView} /> */}
        </p>
      </div>
    </div>
  );
};

// Update the values section to use the new component:
const ValuesSection = () => (
  <>
    <Heading text="Values"></Heading>
    <div className="values-section">
      <ValueItem
        number="0"
        keyword="Wartime sense of urgency"
        content="we believe crunch-time is now, and that we need to bring extraordinary commitment and move blazingly fast to accomplish our mission"
      />
      <ValueItem
        number="1"
        keyword="Greatness"
        content="we're builders at heart and seek to build things that are not just good, but Great. We seek engineers who dare, and believe that for many extremely talented people the rate-limiting step in achieving more is not skill, but attacking harder problems"
      />
      <ValueItem
        number="2"
        keyword="Integrity"
        content="we care deeply about doing the right thing and standing up for what we believe in, whichever way the wind blows"
      />
      <ValueItem
        number="3"
        keyword="Openness"
        content="we want to advance the field and ecosystems of security, verification, and assurance, and core to doing so is sharing work and key standards that the community can build upon."
      />
      <ValueItem
        number="4"
        keyword="Units"
        content="we believe the key to solving extremely difficult problems is 'units': small and vertically integrated teams with a lot of autonomy"
      />
      <ValueItem
        number="5"
        keyword="AGI-native"
        content="we build on a tech stack, and make design choices, that by default get better as AI becomes more capable"
      />
    </div>
  </>
);

// Update the HeadingProps interface
interface HeadingProps {
  text?: string;
  size?: "h1" | "h2" | "h3" | "custom";
  children?: React.ReactNode;
  className?: string;
  // Add this to handle cases where we don't want a heading element
  customContent?: React.ReactNode;
}

const Heading: React.FC<HeadingProps> = ({
  text,
  size = "h3",
  children,
  className = "",
  customContent,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [isUnscrambled, setIsUnscrambled] = useState(false);

  // Helper function to render the scrambled content
  const renderScrambledContent = (content: string) => (
    <ScrambleText
      text={content}
      inView={inView}
      onComplete={() => setIsUnscrambled(true)}
    />
  );

  return (
    <div ref={ref} className={className}>
      {customContent ? (
        // For custom content (like home page intro)
        <>
          {React.cloneElement(customContent as React.ReactElement, {
            inView,
            onComplete: () => setIsUnscrambled(true),
          })}
          {isUnscrambled && (
            <div
              className={`heading-children ${isUnscrambled ? "visible" : ""}`}
            >
              {children}
            </div>
          )}
        </>
      ) : (
        // For regular headings
        <>
          {size === "h1" && <h1>{renderScrambledContent(text!)}</h1>}
          {size === "h2" && <h2>{renderScrambledContent(text!)}</h2>}
          {size === "h3" && <h3>{renderScrambledContent(text!)}</h3>}
          {isUnscrambled && (
            <div
              className={`heading-children ${isUnscrambled ? "visible" : ""}`}
            >
              {children}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Two-color flags: official geometry (13-stripe US with 50-star canton,
// counterchanged Union Jack), palette mapped to the theme — red/blue
// become the accent color, white becomes the page background.
const FlagUS = () => (
  <svg className="loc-flag" viewBox="0 0 60 30" role="img" aria-label="United States">
    <g fill="currentColor">
      <rect x="0" y="0.00" width="60" height="2.31" /><rect x="0" y="4.62" width="60" height="2.31" /><rect x="0" y="9.23" width="60" height="2.31" /><rect x="0" y="13.85" width="60" height="2.31" /><rect x="0" y="18.46" width="60" height="2.31" /><rect x="0" y="23.08" width="60" height="2.31" /><rect x="0" y="27.69" width="60" height="2.31" />
      <rect x="0" y="0" width="24" height="16.15" />
    </g>
    <g fill="var(--background-color)">
      <circle cx="2.00" cy="1.62" r="0.85" /><circle cx="6.00" cy="1.62" r="0.85" /><circle cx="10.00" cy="1.62" r="0.85" /><circle cx="14.00" cy="1.62" r="0.85" /><circle cx="18.00" cy="1.62" r="0.85" /><circle cx="22.00" cy="1.62" r="0.85" /><circle cx="4.00" cy="3.23" r="0.85" /><circle cx="8.00" cy="3.23" r="0.85" /><circle cx="12.00" cy="3.23" r="0.85" /><circle cx="16.00" cy="3.23" r="0.85" /><circle cx="20.00" cy="3.23" r="0.85" /><circle cx="2.00" cy="4.85" r="0.85" /><circle cx="6.00" cy="4.85" r="0.85" /><circle cx="10.00" cy="4.85" r="0.85" /><circle cx="14.00" cy="4.85" r="0.85" /><circle cx="18.00" cy="4.85" r="0.85" /><circle cx="22.00" cy="4.85" r="0.85" /><circle cx="4.00" cy="6.46" r="0.85" /><circle cx="8.00" cy="6.46" r="0.85" /><circle cx="12.00" cy="6.46" r="0.85" /><circle cx="16.00" cy="6.46" r="0.85" /><circle cx="20.00" cy="6.46" r="0.85" /><circle cx="2.00" cy="8.08" r="0.85" /><circle cx="6.00" cy="8.08" r="0.85" /><circle cx="10.00" cy="8.08" r="0.85" /><circle cx="14.00" cy="8.08" r="0.85" /><circle cx="18.00" cy="8.08" r="0.85" /><circle cx="22.00" cy="8.08" r="0.85" /><circle cx="4.00" cy="9.69" r="0.85" /><circle cx="8.00" cy="9.69" r="0.85" /><circle cx="12.00" cy="9.69" r="0.85" /><circle cx="16.00" cy="9.69" r="0.85" /><circle cx="20.00" cy="9.69" r="0.85" /><circle cx="2.00" cy="11.31" r="0.85" /><circle cx="6.00" cy="11.31" r="0.85" /><circle cx="10.00" cy="11.31" r="0.85" /><circle cx="14.00" cy="11.31" r="0.85" /><circle cx="18.00" cy="11.31" r="0.85" /><circle cx="22.00" cy="11.31" r="0.85" /><circle cx="4.00" cy="12.92" r="0.85" /><circle cx="8.00" cy="12.92" r="0.85" /><circle cx="12.00" cy="12.92" r="0.85" /><circle cx="16.00" cy="12.92" r="0.85" /><circle cx="20.00" cy="12.92" r="0.85" /><circle cx="2.00" cy="14.54" r="0.85" /><circle cx="6.00" cy="14.54" r="0.85" /><circle cx="10.00" cy="14.54" r="0.85" /><circle cx="14.00" cy="14.54" r="0.85" /><circle cx="18.00" cy="14.54" r="0.85" /><circle cx="22.00" cy="14.54" r="0.85" />
    </g>
  </svg>
);

const FlagUK = () => (
  <svg className="loc-flag" viewBox="0 0 60 30" role="img" aria-label="United Kingdom">
    <clipPath id="ukq">
      <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
    </clipPath>
    <rect width="60" height="30" fill="currentColor" />
    <path d="M0,0 60,30 M60,0 0,30" stroke="var(--background-color)" strokeWidth="6" />
    <path d="M0,0 60,30 M60,0 0,30" clipPath="url(#ukq)" stroke="currentColor" strokeWidth="4" />
    <path d="M30,0 v30 M0,15 h60" stroke="var(--background-color)" strokeWidth="10" />
    <path d="M30,0 v30 M0,15 h60" stroke="currentColor" strokeWidth="6" />
  </svg>
);

// Pages reachable at "/<slug>". "none" is the home page at "/".
const URL_PAGES = [
  "company",
  "technology",
  "join",
  "contact",
  "updates",
  "c1",
] as const;
type Page = "none" | (typeof URL_PAGES)[number];

// Camera + auto-forward per page. A delayMs row waits before enabling
// auto-forward; a row without one applies autoForward immediately.
const PAGE_SCENE: Record<
  Page | "notFound",
  { camera: string; autoForward: boolean; delayMs?: number }
> = {
  none: { camera: "under", autoForward: true, delayMs: 1200 },
  company: { camera: "isolators", autoForward: true, delayMs: 600 },
  technology: { camera: "system", autoForward: false },
  join: { camera: "join", autoForward: true },
  updates: { camera: "system", autoForward: true },
  contact: { camera: "system", autoForward: false },
  c1: { camera: "under", autoForward: true },
  notFound: { camera: "under", autoForward: false },
};

function MainContent() {
  const cameraControls: any = useRef<CameraControls | undefined>();
  const [darkMode, setDarkMode] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [viewedPage, setViewedPage] = useState<Page | "notFound">("none");
  const [autoForward, setAutoForward] = useState(false);
  const initialLoadRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();

  const [currentCamera, switchCamera] = useStore(
    useShallow((state: any) => [state.currentCamera, state.switchCamera]),
  );

  const applyScene = useCallback(
    (page: Page | "notFound") => {
      const scene = PAGE_SCENE[page];
      if (!scene) return;

      switchCamera(scene.camera);
      if (scene.delayMs !== undefined) {
        setTimeout(() => {
          setAutoForward(scene.autoForward);
        }, scene.delayMs);
      } else {
        setAutoForward(scene.autoForward);
      }
    },
    [switchCamera],
  );

  // Flip data-theme on the root <html> (or body) each time darkMode changes
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      darkMode ? "dark" : "light",
    );
  }, [darkMode]);

  // A simple effect that runs exactly once on mount
  useEffect(() => {
    if (initialLoadRef.current) {
      setAutoForward(true);

      initialLoadRef.current = false;
    }
  }, []);

  const handleNavigation = (page: Page) => {
    setViewedPage(page);
    setShowMenu(false);
    window.scrollTo({ top: 0, behavior: "smooth" });

    const url = page === "none" ? "/" : `/${page}`;
    navigate(url, { replace: true });

    applyScene(page);
  };

  useEffect(() => {
    // Normalize trailing slashes so "/technology/" matches "/technology".
    // Vercel also 308s trailing slashes at the edge (trailingSlash: false);
    // this keeps the client correct even without that layer.
    const path = location.pathname.replace(/\/+$/, "") || "/";
    const slug = path.slice(1);
    const page: Page | "notFound" =
      path === "/"
        ? "none"
        : (URL_PAGES as readonly string[]).includes(slug)
          ? (slug as Page)
          : "notFound";
    setViewedPage(page);
    applyScene(page);
  }, [location.pathname, applyScene]);

  return (
    <div className="theme-transition">
      <div className="app-wrapper">
        <div className="App">
          <header className="site-header">
            <div
              className="logo-container"
              onClick={() => handleNavigation("none")}
            >
              <div
                className="header-logo"
                role="img"
                aria-label="Ulyssean"
                style={{
                  WebkitMask: `url(${process.env.PUBLIC_URL}/logo_new.svg) no-repeat center / contain`,
                  mask: `url(${process.env.PUBLIC_URL}/logo_new.svg) no-repeat center / contain`,
                }}
              />
            </div>
            <div className="header-controls">
              {/* @ts-ignore */}
              <Classic
                toggled={darkMode}
                toggle={() => setDarkMode(!darkMode)}
                duration={700}
                style={{
                  color: "var(--invert-background-color)",
                }}
              />
              {/* hiding hamburger menu for now */}
              {/* <button
                className="hamburger-menu"
                onClick={() => setShowMenu(!showMenu)}
              >
                <FaBars size={20} />
              </button> */}
            </div>

            {showMenu && (
              <>
                <div
                  className="menu-overlay"
                  onClick={() => setShowMenu(false)}
                />
                <div className="hamburger-menu-content">
                  <ul>
                    <li onClick={() => handleNavigation("none")}>Home</li>
                    <li onClick={() => handleNavigation("company")}>Company</li>
                    <li onClick={() => handleNavigation("updates")}>Updates</li>
                    <li onClick={() => handleNavigation("technology")}>
                      Technology
                    </li>
                    <li onClick={() => handleNavigation("join")}>Join</li>
                    {/* <li onClick={() => handleNavigation("contact")}>Contact</li> */}
                  </ul>
                </div>
              </>
            )}
          </header>

          <div className="main-container">
            {viewedPage === "c1" ? (
              <>
                <section className="text-panel c1-panel">
                  <ClusterOne />
                </section>

                {/* <section className="canvas-container">
                  {webGL2Available ? (
                    <Canvas
                      style={{ backgroundColor: "transparent" }}
                      gl={{
                        alpha: true,
                        precision: "highp",
                        toneMapping: THREE.CineonToneMapping,
                      }}
                    >
                      <CameraController
                        currentCamera={currentCamera}
                        cameraControls={cameraControls}
                      />
                      <CameraControls maxDistance={25} ref={cameraControls} />

                      <SceneWithModel darkMode={darkMode} />
                      <WasdControls
                        cameraControls={cameraControls}
                        movementSpeed={
                          cameraSetups[currentCamera].movementSpeed
                        }
                        panSpeed={cameraSetups[currentCamera].panSpeed}
                        autoForward={autoForward}
                        autoDown={false}
                      />
                    </Canvas>
                  ) : null}
                  <LoadingOverlay />
                </section>
                <div className="movement-instructions">
                  <p>{"Tap and drag to move"}</p>
                </div> */}
              </>
            ) : (
              // Normal layout for all other pages
              <>
                <section className="canvas-container">
                  {webGL2Available ? (
                    <Canvas
                      style={{ backgroundColor: "transparent" }}
                      gl={{
                        alpha: true,
                        precision: "highp",
                        toneMapping: THREE.CineonToneMapping,
                      }}
                    >
                      <CameraController
                        currentCamera={currentCamera}
                        cameraControls={cameraControls}
                      />
                      <CameraControls maxDistance={25} ref={cameraControls} />

                      <SceneWithModel darkMode={darkMode} />
                      <WasdControls
                        cameraControls={cameraControls}
                        movementSpeed={
                          cameraSetups[currentCamera].movementSpeed
                        }
                        panSpeed={cameraSetups[currentCamera].panSpeed}
                        autoForward={autoForward}
                        autoDown={viewedPage === "technology"}
                      />
                    </Canvas>
                  ) : null}
                  <LoadingOverlay />
                </section>
                <div className="movement-instructions">
                  <p>{"Tap and drag to move"}</p>
                </div>
                <section className="text-panel">
                  {viewedPage === "none" && (
                    <div>
                      <Heading
                        size="custom"
                        className="home-intro"
                        customContent={
                          <HeroScramble />
                        }
                      >
                        <p className="home-intro-subtext visible"></p>
                        {/* <div className={`latest-update visible`}>
                          <div className="latest-update-header">Updates</div>
                          <Updates
                            limit={1}
                            isFrontpage={true}
                            handleNavigation={handleNavigation}
                          />
                        </div> */}
                      </Heading>
                    </div>
                  )}

                  {viewedPage === "company" && (
                    <div>
                      <Heading text="Founder letter"></Heading>
                      <div className="founder-letter">
                        <p>
                          Ulyssean is an AGI company. Not in the sense of
                          building it. But in the sense of building for a world
                          we expect to be completely reshaped by it.
                        </p>

                        <p>
                          The genie is out of the bottle. AGI is coming very
                          soon, will have an extremely large impact, and society
                          is nowhere near ready.
                        </p>

                        <p>We need to get serious.</p>

                        <p>
                          The other mission critical environment I know is from
                          my training as a pilot. And in comparison, it seems to
                          me there are plenty of companies building infotainment
                          systems for passengers. By comparison, Ulyssean is in
                          the business of thinking about how things go deeply,
                          badly wrong. In some ways I expect us to be a
                          cyberphysical security company. In others, we'll be a
                          verification and assurance company. Regardless, we're
                          about to face a buildout of data centre infrastructure
                          unparalleled in human history: and this infrastructure
                          will serve the heart of national, and global,
                          security.
                        </p>

                        <p>
                          I've been weighing whether to pursue this mission as a
                          company or a foundation. I decided on a company
                          because technology is perhaps the single greatest
                          force for good in all of human history: and the free
                          market is our most efficient force for bringing it to
                          life fast and at scale.
                        </p>

                        <p>
                          "Ulyssean" is how we will operate. We will go wherever
                          needed and build whatever it takes. We will expect
                          strange creatures, turbulent seas, frequent peril: and
                          a relentless resourcefulness to navigate it all and
                          make it back home.
                        </p>

                        <p className="founder-signature">
                          Ἀνάγεσθαι,
                          <br />
                          Jacob
                          <br />
                          Founder
                        </p>
                      </div>
                      <ValuesSection />
                      <Heading text="Community"></Heading>
                      <p className="industry-standards-intro">
                        Ulyssean is a member of the Confidential Computing
                        Consortium, the Coalition for Secure AI and the Flexible
                        Hardware-enabled Guarantees research community. We
                        actively work to contribute to a strong ecosystem around
                        frontier AI security and verification, by engaging with
                        development of standards, open source libraries, and
                        community building.
                      </p>
                      <div className="industry-standards">
                        <a
                          href="https://confidentialcomputing.io/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="consortium-logo"
                        >
                          <img
                            src="/assets/ccc.avif"
                            alt="Confidential Computing Consortium logo"
                          />
                        </a>
                        <a
                          href="https://www.coalitionforsecureai.org/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="consortium-logo"
                        >
                          <img src="/assets/cosai.webp" alt="COSAI logo" />
                        </a>
                        <a
                          href="https://secure-processor-demo.replit.app/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="consortium-logo"
                        >
                          <img src="/assets/flexheg.svg" alt="FlexHEG logo" />
                        </a>
                      </div>
                      <Heading text="Updates"></Heading>
                      <Updates limit={1} handleNavigation={handleNavigation} />
                      <Heading text="Contact"></Heading>
                      <div>
                        <p>Direct inquiries to contact@ulyssean.com</p>
                      </div>
                      <br />
                      <p>Subscribe to updates:</p>
                      <div>
                        <div className="email-subscription">
                          <ContactForm />
                        </div>
                      </div>
                    </div>
                  )}
                  {viewedPage === "technology" && (
                    <div>
                      <Heading text="Technology" size="h2">
                        <p>
                          We pride ourselves on being ridiculously full-stack.
                          Our team has experience with the data center stack
                          from construction itself to training transformers. We
                          conceptualize problems from silicon through kernels to
                          applications, and believe that truly elegant solutions
                          require vertical thinking across interfaces and
                          interactions.
                        </p>
                        <div className="tech-card-grid">
                          {[
                            {
                              title: "Formally Verified Rate-Limiter",
                              notes:
                                "For flexibly introducing rate-limiting in large, security-critical, software-defined networks. A full, minimal networking stack, built on the seL4 microkernel.",
                              stage: "Development",
                              misc: "Platform: seL4, IPv6",
                            },
                            {
                              title: "Trusted Coprocessor",
                              notes:
                                "What does the future data centre control plane look like if we don't want to include the host CPU, hypervisor or hardware in our trust boundary? We are exploring a series of potential interfaces for defining a safeguarded boundary.",
                              stage: "Research",
                              misc: "Platform: ConnectX-7, Bluefield, BMCs",
                            },
                            {
                              title: "AGI Side-Channels",
                              notes:
                                "We're researching the interaction between frontier models and hardware side-channels. Example projects include fine-tuning models to exfiltrate data from airgapped environments by modulating their own power signature, or breaking network topologies by modulating FPGAs on network cards to create makeshift antennas and receivers.",
                              stage: "Research",
                              misc: "Security research",
                            },
                            {
                              title: "Verification sensors",
                              notes:
                                "How can operators of mission-critical data centres externally verify the state and the integrity of their workloads, in a privacy-protecting way? We're exploring the use of retrofittable, cheap, and low-power sensors, that are simple enough to be deeply auditable and secure.",
                              stage: "Research",
                              misc: "Verification research",
                            },
                            {
                              title: "Box",
                              notes:
                                "Production grade data-centre hardware in the form factor of a portable hardcase. End-to-end demonstration and research platform for flexible hardware-enabled guarantees.",
                              stage: "Built",
                              misc: "H100 80GB PCIe, infiniband, Connect-X 6, AMD 9334 with SEV-SNP confidential computing",
                            },
                          ].map((card, index) => (
                            <TechCard key={index} {...card} index={index} />
                          ))}
                        </div>
                      </Heading>
                    </div>
                  )}
                  {viewedPage === "join" && (
                    <div>
                      <Heading text="Join Ulyssean" size="h2">
                        <blockquote className="shackleton-quote">
                          <p>
                            "Men wanted for hazardous journey, small wages,
                            bitter cold, long months of complete darkness,
                            constant danger, safe return doubtful, honour and
                            recognition in case of success."
                          </p>
                          <footer>
                            — ad for Ernest Shackleton's antarctic expedition,
                            London Times, December 29th, 1913
                          </footer>
                        </blockquote>

                        <iframe
                          title="Join"
                          className="airtable-embed"
                          src="https://airtable.com/embed/appmqFZbWRqyDnJ1h/paglJTqfg1WoovvbQ/form"
                          frameBorder="0"
                          width="100%"
                          height="533"
                          style={{
                            background: "transparent",
                            border: "1px solid #ccc",
                          }}
                        ></iframe>

                        <iframe
                          title="Roles"
                          className="airtable-embed"
                          src="https://airtable.com/embed/appmqFZbWRqyDnJ1h/shr05qM84jF0SwRdb?viewControls=on"
                          frameBorder="0"
                          width="100%"
                          height="533"
                          style={{
                            background: "transparent",
                            border: "1px solid #ccc",
                          }}
                        ></iframe>
                      </Heading>
                    </div>
                  )}
                  {viewedPage === "updates" && (
                    <div>
                      <Heading text="Updates" size="h2">
                        <Updates handleNavigation={handleNavigation} />
                      </Heading>
                    </div>
                  )}
                  {viewedPage === "notFound" && (
                    <div>
                      <Heading text="404 — Not found" size="h2">
                        <p>
                          There is no page at this address.{" "}
                          <a href="/">Return home.</a>
                        </p>
                      </Heading>
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </div>

        {/* <footer className="site-footer">
          <ul className="footer-nav">
            <li onClick={() => handleNavigation("company")}>Company</li>
            <li onClick={() => handleNavigation("technology")}>Technology</li>
            <li onClick={() => handleNavigation("join")}>Join</li>
          </ul>
          <div className="footer-copyright">© 2025 Ulyssean PBC</div>
        </footer> */}
        <footer className="locations-footer">
          <span className="footer-theme-toggle">
            {/* @ts-ignore */}
            <Classic
              toggled={darkMode}
              toggle={() => setDarkMode(!darkMode)}
              duration={700}
              style={{ color: "var(--gray-color)" }}
            />
          </span>
          <span>For the lightcone, built in</span>
          <FlagUS />
          <span className="loc-sep">/</span>
          <FlagUK />
        </footer>
      </div>
    </div>
  );
}

function App() {
  return <MainContent />;
}

function AppWrapper() {
  return (
    <Router>
      <Routes>
        <Route path="/*" element={<App />} />
      </Routes>
    </Router>
  );
}

export default AppWrapper;
